import { StatsBox } from "./stats-box";
import { useEffect, useState, useCallback } from "react";
import PuffLoader from "react-spinners/PuffLoader";
import moment from "moment";
import { createSeries, fillUnfilledSeries, calculatePercentageValue, getPostsForDates, countProfileViews, findLastFoundValueCountIndex } from "./utils";
import { formatNbPosts, formatFollowers, formatLikes, formatEngagement } from "./statsFormatter";

import {
  Box,
} from "@chakra-ui/react";

export const SquaresPanel = ({
  accountStats,
  prevPeriodAccountStats = [] as any,
  tweetStats,
  prevPeriodTweetStats = [] as any,
  timeFrame,
  prevTimeFrame = {} as any,
  previousDayStat,
  isLoadingAccountStats = true,
  isLoadingTweetStats = true,
  mode = "personal",
  onClickCallback = (...args: any | undefined) => { },
  isFirstLoad = true,
  takeScreenshot = undefined as any,
  disableComparison = false,
}) => {
  const [followerLineSeriesData, setfollowerLineSeriesData] = useState<any>([]);
  const [engagementLineSeriesData, setEngagementLineSeriesData] = useState<any>([]);
  const [postsLineSeriesData, setPostsLineSeriesData] = useState<any>([]);
  const [commentsLineSeriesData, setCommentsLineSeriesData] = useState<any>([]);
  const [impressionLineSeriesData, setImpressionLineSeriesData] = useState<any>([]);
  const [profileVisitsLineSeriesData, setProfileVisitsLineSeriesData] = useState<any>([]);
  const [likesLineSeriesData, setLikesLineSeriesData] = useState<any>([]);
  const [sharesLineSeriesData, setSharesLineSeriesData] = useState<any>([]);
  const [boxDataLoading, setBoxDataLoading] = useState<any>(true);
  const [boxNumbers, setBoxNumbers] = useState({
    followers: accountStats?.length ? accountStats[accountStats.length - 1]?.followerCount : 0,
    prevFollowers: prevPeriodAccountStats?.length ? prevPeriodAccountStats[prevPeriodAccountStats.length - 1]?.followerCount : 0,
    numberPosts: tweetStats?.length || 0,
    impressions: 0,
    profileVisits: 0,
    numberComments: 0,
    numberLikes: 0,
    numberShares: 0,
    engagement: 0,
    prevImpressions: 0,
    prevProfileVisits: 0,
    prevNumberComments: 0,
    prevNumberLikes: 0,
    prevNumberShares: 0,
    prevEngagement: 0,
    prevNumberPosts: prevPeriodTweetStats?.length || 0,
  });

  const [boxPercentages, setBoxPercentages] = useState({
    followerChangePercent: 0,
    engagementChangePercentage: 0,
    postChangePercentage: 0,
    commentsChangePercentage: 0,
    impressionsChangePercentage: 0,
    likesChangePercentage: 0,
    sharesChangePercentage: 0,
    profileVisitsChangePercentage: 0,
  });

  //Followers
  useEffect(() => {
    let followerLineSeries = formatFollowers(accountStats, timeFrame, mode);
    setfollowerLineSeriesData([...followerLineSeries]);
  }, [accountStats, timeFrame, mode]);

  //engagement
  useEffect(() => {
    let engagementLineSeries = formatEngagement(tweetStats, timeFrame, mode) as any;
    setEngagementLineSeriesData([...engagementLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  //posts
  useEffect(() => {
    let postsLineSeries = formatNbPosts(tweetStats, timeFrame, mode);
    setPostsLineSeriesData([...postsLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  //comments
  useEffect(() => {
    let commentsLineSeries = createSeries(timeFrame);
    let currentCommentCount = 0;
    commentsLineSeries = commentsLineSeries.map((dataPoint, i, cSeries) => {
      if (tweetStats && tweetStats.length > 0) {
        let datedTweets = getPostsForDates(tweetStats, mode, dataPoint);

        if (datedTweets.length > 0) {
          currentCommentCount = dataPoint.commentCount = datedTweets.reduce(
            (prevValue, currentValue, i) => {
              return prevValue += ((datedTweets?.[i]?.numComments ?? 0))
            }, 0
          );
          dataPoint.y = currentCommentCount;
        } else {
          dataPoint.commentCount = dataPoint.y = 0;
        }
      }
      return dataPoint;
    });

    commentsLineSeries.map((dp, i) => {
      dp.y = dp.commentCount = (dp.y) ? dp.commentCount : 0;
      dp.x = i + 1;
    });

    setCommentsLineSeriesData([...commentsLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  //impression
  useEffect(() => {
    let impressionLineSeries = createSeries(timeFrame);
    let currentImpressionCount = 0;
    impressionLineSeries = impressionLineSeries.map((dataPoint, i, iSeries) => {
      if (tweetStats && tweetStats.length > 0) {
        let datedTweets = getPostsForDates(tweetStats, mode, dataPoint);

        if (datedTweets.length > 0) {
          currentImpressionCount = dataPoint.impressions = datedTweets.reduce(
            (prevValue, currentValue, i) => {
              return prevValue += ((datedTweets?.[i]?.numViews ?? 0))
            }, 0
          );
          dataPoint.y = currentImpressionCount;
          //fillUnfilledSeries(currentImpressionCount, i, impressionLineSeries, "impressions");
        } else if (i == iSeries.length - 1) {
          dataPoint.y = dataPoint.impressions = 0
          //fillUnfilledSeries(currentImpressionCount, i, impressionLineSeries, "impressions");
        }
      }
      return dataPoint;
    });

    impressionLineSeries.map((dp, i) => {
      dp.y = dp.impressions = dp.impressions || 0;
      dp.x = i + 1;
    });

    setImpressionLineSeriesData([...impressionLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  //profile views
  useEffect(() => {
    let profileVisitsLineSeries = createSeries(timeFrame);
    let currentProfileVisits = 0;
    profileVisitsLineSeries = profileVisitsLineSeries.map((dataPoint, i, pvSeries) => {
      if (accountStats && accountStats.length > 0) {
        let index = accountStats.findIndex((n) => {
          const toCheckDateAgainst = (mode == "personal")
            ? (n.dateFetched.toDate ? n.dateFetched.toDate() : n.dateFetched)
            : new Date().setTime(n.dateFetched?._seconds * 1000)
            ;
          return moment.utc(toCheckDateAgainst)?.format("MMM. Do ’YY") === dataPoint.date;
        });

        if (index != -1) {
          currentProfileVisits = dataPoint.viewCounts =
            (accountStats?.[index]?.viewCounts?.views)
              ? accountStats?.[index]?.viewCounts?.views ?? currentProfileVisits
              : accountStats?.[index]?.viewCounts ?? currentProfileVisits; //some viewCounts are missing from some users stats, same like followerCounts.
          dataPoint.y = currentProfileVisits;
          fillUnfilledSeries(currentProfileVisits, i, profileVisitsLineSeries, "viewCounts");
        } else if (i == pvSeries.length - 1) {
          fillUnfilledSeries(currentProfileVisits, i, profileVisitsLineSeries, "viewCounts");
        }
      }
      return dataPoint;
    });
    profileVisitsLineSeries.map((dp, i) => {
      dp.y = dp.viewCounts = dp.viewCounts || 0;
      dp.x = i + 1;
    });
    //console.log(profileVisitsLineSeries,currentProfileVisits, 'view line series')
    setProfileVisitsLineSeriesData([...profileVisitsLineSeries]);
  }, [accountStats, timeFrame, mode]);


  //likes line series
  useEffect(() => {
    let likesLineSeries = formatLikes(tweetStats, timeFrame, mode) as any;
    setLikesLineSeriesData([...likesLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  //shares line series
  useEffect(() => {
    let sharesLineSeries = createSeries(timeFrame);
    let currentSharesCount = 0;
    sharesLineSeries = sharesLineSeries.map((dataPoint, i, lSeries) => {
      if (tweetStats && tweetStats.length > 0) {
        let datedTweets = getPostsForDates(tweetStats, mode, dataPoint);

        if (datedTweets.length > 0) {
          currentSharesCount = dataPoint.shares = datedTweets.reduce(
            (prevValue, currentValue, i) => {
              return prevValue += ((datedTweets?.[i]?.totalShares ?? 0))
            }, 0
          );
          dataPoint.y = currentSharesCount;

          //fillUnfilledSeries(currentSharesCount, i, sharesLineSeries, "shares");
        } else {
          dataPoint.y = dataPoint.shares = 0;
          //fillUnfilledSeries(currentSharesCount, i, sharesLineSeries, "shares");
        }
      }
      return dataPoint;
    });

    sharesLineSeries.map((dp, i) => {
      dp.y = dp.shares = dp.shares || 0;
      dp.x = i + 1;
    });

    setSharesLineSeriesData([...sharesLineSeries]);
  }, [tweetStats, timeFrame, mode]);

  useEffect(() => {
    setBoxDataLoading(true);
    let newBoxNumbers = {

      followers: accountStats?.length ? accountStats[findLastFoundValueCountIndex(accountStats, 'followerCount')]?.followerCount : 0,
      prevFollowers: prevPeriodAccountStats?.length ? prevPeriodAccountStats[findLastFoundValueCountIndex(prevPeriodAccountStats, 'followerCount')]?.followerCount : 0,
      numberPosts: tweetStats?.length || 0,
      impressions: 0,
      profileVisits: 0,
      numberComments: 0,
      numberLikes: 0,
      numberShares: 0,
      engagement: 0,
      prevImpressions: 0,
      prevProfileVisits: 0,
      prevNumberComments: 0,
      prevNumberLikes: 0,
      prevNumberShares: 0,
      prevEngagement: 0,
      prevNumberPosts: prevPeriodTweetStats?.length || 0,
    };

    let newBoxPercentages = {
      followerChangePercent: 0,
      engagementChangePercentage: 0,
      postChangePercentage: 0,
      commentsChangePercentage: 0,
      impressionsChangePercentage: 0,
      likesChangePercentage: 0,
      sharesChangePercentage: 0,
      profileVisitsChangePercentage: 0,
    };

    if (newBoxNumbers.numberPosts > 0) {
      tweetStats.forEach((tweet) => {
        if (tweet?.numViews) newBoxNumbers.impressions += tweet?.numViews;
        newBoxNumbers.engagement += (tweet?.numLikes ?? 0) + (tweet?.totalShares ?? 0) + (tweet?.numComments ?? 0);
        newBoxNumbers.numberComments += (tweet?.numComments ?? 0);
        newBoxNumbers.numberLikes += (tweet?.numLikes ?? 0);
        newBoxNumbers.numberShares += (tweet?.totalShares ?? 0);
      });
    }

    if (newBoxNumbers.prevNumberPosts > 0) {
      prevPeriodTweetStats.forEach((tweet) => {
        if (tweet?.numViews) newBoxNumbers.prevImpressions += tweet?.numViews;
        newBoxNumbers.prevEngagement += (tweet?.numLikes ?? 0) + (tweet?.totalShares ?? 0) + (tweet?.numComments ?? 0);
        newBoxNumbers.prevNumberComments += (tweet?.numComments ?? 0);
        newBoxNumbers.prevNumberLikes += (tweet?.numLikes ?? 0);
        newBoxNumbers.prevNumberShares += (tweet?.totalShares ?? 0);
      });
    }

    if (accountStats && accountStats?.length > 0) {
      newBoxNumbers.profileVisits = countProfileViews(accountStats);
    }

    if (prevPeriodAccountStats && prevPeriodAccountStats?.length > 0) {
      newBoxNumbers.prevProfileVisits = countProfileViews(prevPeriodAccountStats);
    }

    //followers %
    //@ts-ignore
    newBoxPercentages.followerChangePercent = calculatePercentageValue(newBoxNumbers.followers, newBoxNumbers.prevFollowers);
    //debugger;
    //engagement %
    //const engagementChange = newBoxNumbers.engagement - newBoxNumbers.prevEngagement || 0;
    //@ts-ignore
    //newBoxPercentages.engagementChangePercentage = (newBoxNumbers.engagement != 0)? Number(engagementChange / newBoxNumbers.engagement * 100).toFixed(2) || 0 : ((newBoxNumbers.engagement!=0)? 100 : 0) ;
    newBoxPercentages.engagementChangePercentage = calculatePercentageValue(newBoxNumbers.engagement, newBoxNumbers.prevEngagement);

    //post %
    //@ts-ignore
    newBoxPercentages.postChangePercentage = calculatePercentageValue(newBoxNumbers.numberPosts, newBoxNumbers.prevNumberPosts);

    //comments
    //@ts-ignore
    newBoxPercentages.commentsChangePercentage = calculatePercentageValue(newBoxNumbers.numberComments, newBoxNumbers.prevNumberComments);

    //impressions
    //@ts-ignore
    newBoxPercentages.impressionsChangePercentage = calculatePercentageValue(newBoxNumbers.impressions, newBoxNumbers.prevImpressions)

    //profile views
    //@ts-ignore
    newBoxPercentages.profileVisitsChangePercentage = calculatePercentageValue(newBoxNumbers.profileVisits, newBoxNumbers.prevProfileVisits);

    //likes
    //@ts-ignore
    newBoxPercentages.likesChangePercentage = calculatePercentageValue(newBoxNumbers.numberLikes, newBoxNumbers.prevNumberLikes);

    //shares
    //@ts-ignore
    newBoxPercentages.sharesChangePercentage = calculatePercentageValue(newBoxNumbers.numberShares, newBoxNumbers.prevNumberShares);

    setBoxNumbers({ ...newBoxNumbers });
    setBoxPercentages({ ...newBoxPercentages });
    setBoxDataLoading(false);
  }, [tweetStats, accountStats, timeFrame, prevPeriodAccountStats, prevPeriodTweetStats, prevTimeFrame, mode]);

  useEffect(() => {
    onClickCallback({ type: "Followers", stats: followerLineSeriesData, labelValue: boxNumbers?.followers, percentageValue: boxPercentages?.followerChangePercent });
  }, [followerLineSeriesData, boxNumbers, boxPercentages]);

  const onStatsBoxClick = useCallback(
    (statsBoxObj) => {
      onClickCallback(statsBoxObj);
    }, [tweetStats, accountStats, timeFrame, prevPeriodAccountStats, prevPeriodTweetStats, prevTimeFrame, mode]);

  const onScreenshot = useCallback(
    (statsBoxObj) => {
      takeScreenshot({ data: statsBoxObj.data, screenshotType: "stats_box_graph" });
    }, [tweetStats, accountStats, timeFrame, prevPeriodAccountStats, prevPeriodTweetStats, prevTimeFrame, mode]);

  // console.group('timeframe and other checks');
  // console.log('timeFrame:', timeFrame, accountStats);
  // console.log('prevTimeFrame:', prevTimeFrame, prevPeriodAccountStats);
  // console.groupEnd();

  return (
    <>
      {
        (isLoadingTweetStats || isLoadingAccountStats || boxDataLoading) ?
          (
            <Box maxW={1200}>
              <PuffLoader
                color={"#0A66C2"}
                loading={isLoadingAccountStats}
                cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }}
                size={50}
              />
            </Box>
          )
          :
          (
            <Box
              /*keeping margins in responsive form below for any further changes - manoj*/
              // ml={["20px", "5px", "5px", "-27px"]} 
              // mb={["5px", "5px", "5px", "5px"]} 
              display="grid"
              // gridTemplateColumns={[
              //   "171px 171px",
              //   "300px 300px",
              //   "220px 220px 220px",
              //   "220px 220px 220px 220px",
              //   "300px 300px 300px 300px"
              // ]} 
              gridTemplateColumns={[
                "1fr 1fr",
                "1fr 1fr",
                "1fr 1fr",
                "1fr 1fr 1fr",
                "1fr 1fr 1fr 1fr",
              ]}
              gap={[2, 4, 6]}
              justifyItems="center"
              mt={10}
            >
              <StatsBox
                data={followerLineSeriesData}
                labelValue={boxNumbers.followers}
                percentageValue={boxPercentages.followerChangePercent}
                title="Followers"
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={engagementLineSeriesData}
                labelValue={boxNumbers.engagement}
                title="Engagements"
                percentageValue={boxPercentages.engagementChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={postsLineSeriesData}
                labelValue={boxNumbers.numberPosts}
                title="Posts"
                percentageValue={boxPercentages.postChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={commentsLineSeriesData}
                labelValue={boxNumbers.numberComments}
                title="Comments"
                percentageValue={boxPercentages.commentsChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={impressionLineSeriesData}
                labelValue={boxNumbers.impressions}
                title="Impressions"
                percentageValue={boxPercentages.impressionsChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={profileVisitsLineSeriesData}
                labelValue={boxNumbers.profileVisits}
                title="Profile Views"
                percentageValue={boxPercentages.profileVisitsChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={likesLineSeriesData}
                labelValue={boxNumbers.numberLikes}
                title="Likes"
                percentageValue={boxPercentages.likesChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />
              <StatsBox
                data={sharesLineSeriesData}
                labelValue={boxNumbers.numberShares}
                title="Shares"
                percentageValue={boxPercentages.sharesChangePercentage}
                onClickCallback={onStatsBoxClick}
                isClickable={true}
                canScreenshot={!!takeScreenshot}
                onScreenshot={onScreenshot}
                disableComparison={disableComparison}
              />

            </Box>
          )
      }
    </>
  );
}